import type { ComponentProps } from 'react'
import { cn, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'

import { Slot } from './Slot'

export const cardVariants = tv({
  base: 'relative w-full border text-left',

  defaultVariants: {
    size: 'md',
    variant: 'default',
  },

  variants: {
    height: {
      fill: 'h-full',
    },

    overflow: {
      clip: 'overflow-x-clip',
      hidden: 'overflow-hidden',
    },

    padding: {
      lg: 'p-8',
      md: 'p-6',
      none: 'p-0',
      sm: 'p-4',
      xs: 'p-2',
    },

    size: {
      lg: 'rounded-lg',
      md: 'rounded-md',
      sm: 'rounded-md',
      xs: 'rounded-md',
    },

    variant: {
      default: 'bg-tremor-background shadow-xs',
      error: 'border-category-red/40 bg-category-red/10',
      warning: 'border-tremor-brand-subtle bg-tremor-brand-faint',
      well: 'bg-tremor-background-muted dark:bg-tremor-background-warm',
    },
  },
})

const CARD_SIZE_PADDING = {
  lg: 'lg',
  md: 'md',
  sm: 'sm',
  xs: 'xs',
} as const

export interface CardProps
  extends
    ComponentProps<'div'>,
    Omit<VariantProps<typeof cardVariants>, 'padding'> {
  asChild?: boolean
  padding?: 'none'
}

function Card({
  asChild,
  className,
  height,
  overflow,
  padding,
  size = 'md',
  variant,
  ...props
}: CardProps) {
  const Component = asChild ? Slot : 'div'
  const resolvedPadding = padding ?? CARD_SIZE_PADDING[size]
  return (
    <Component
      className={cn(
        cardVariants({
          height,
          overflow,
          padding: resolvedPadding,
          size,
          variant,
        }),
        className,
      )}
      {...props}
    />
  )
}

export { Card }
